import { useEffect, useRef, useState } from "react";
import { useLazyDownloadFileQuery } from "../../features/fileSystem/filesStorage";
import { FileItem } from "../../types/interfaces/filesStorage";
import { PreviewSource, previewSourceKey } from "./previewSource";

interface CacheEntry {
  blob: Blob;
  url: string;
  refCount: number;
  expiresAt: number;
}

const MAX_ENTRIES = 5;
const TTL_MS = 50 * 60 * 1000;

const cache: Map<string, CacheEntry> = new Map();

function evictExpired() {
  const now = Date.now();
  Array.from(cache.entries()).forEach(([key, entry]) => {
    if (entry.refCount > 0) return;
    if (entry.expiresAt < now) {
      URL.revokeObjectURL(entry.url);
      cache.delete(key);
    }
  });
}

function evictLRU() {
  if (cache.size <= MAX_ENTRIES) return;
  const candidates = Array.from(cache.entries())
    .filter(([, e]) => e.refCount <= 0)
    .sort((a, b) => a[1].expiresAt - b[1].expiresAt);
  const toRemove = cache.size - MAX_ENTRIES;
  for (let i = 0; i < toRemove && i < candidates.length; i++) {
    const [key, entry] = candidates[i];
    URL.revokeObjectURL(entry.url);
    cache.delete(key);
  }
}

function acquire(key: string): CacheEntry | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (entry.expiresAt < Date.now()) {
    if (entry.refCount <= 0) {
      URL.revokeObjectURL(entry.url);
      cache.delete(key);
    }
    return null;
  }
  entry.refCount += 1;
  return entry;
}

function release(key: string) {
  const entry = cache.get(key);
  if (!entry) return;
  entry.refCount = Math.max(0, entry.refCount - 1);
  evictExpired();
  evictLRU();
}

function store(key: string, blob: Blob): CacheEntry {
  const url = URL.createObjectURL(blob);
  const entry: CacheEntry = {
    blob,
    url,
    refCount: 1,
    expiresAt: Date.now() + TTL_MS,
  };
  cache.set(key, entry);
  evictLRU();
  return entry;
}

interface BlobLoaderState {
  loading: boolean;
  error: string | null;
  blob: Blob | null;
  url: string | null;
  retry: () => void;
}

interface BlobLoaderOptions {
  stream?: boolean;
}

export function useBlobLoader(
  source: PreviewSource | null,
  options?: BlobLoaderOptions,
): BlobLoaderState {
  const stream = options?.stream ?? false;
  const [state, setState] = useState<{
    loading: boolean;
    error: string | null;
    blob: Blob | null;
    url: string | null;
  }>({ loading: false, error: null, blob: null, url: null });

  const [triggerDownloadFile] = useLazyDownloadFileQuery();
  const [attempt, setAttempt] = useState(0);
  const cancelledRef = useRef(false);
  const acquiredKeyRef = useRef<string | null>(null);

  useEffect(() => {
    cancelledRef.current = false;

    if (!source) {
      setState({ loading: false, error: null, blob: null, url: null });
      return () => {
        cancelledRef.current = true;
      };
    }

    if (source.kind === "inline") {
      const url = URL.createObjectURL(source.blob);
      setState({ loading: false, error: null, blob: source.blob, url });
      return () => {
        cancelledRef.current = true;
        URL.revokeObjectURL(url);
      };
    }

    const item: FileItem = source.item;
    const key = previewSourceKey(source);
    setState({ loading: true, error: null, blob: null, url: null });

    const cached = acquire(key);
    if (cached) {
      acquiredKeyRef.current = key;
      setState({
        loading: false,
        error: null,
        blob: cached.blob,
        url: cached.url,
      });
      return () => {
        cancelledRef.current = true;
        if (acquiredKeyRef.current) {
          release(acquiredKeyRef.current);
          acquiredKeyRef.current = null;
        }
      };
    }

    const run = async () => {
      try {
        const response = await triggerDownloadFile({
          source: item.fsource,
          fileName: item.fname,
        }).unwrap();

        if (cancelledRef.current) return;

        const url = response?.file_urls?.[0];
        if (!url) {
          setState({
            loading: false,
            error: "No download URL returned by the server",
            blob: null,
            url: null,
          });
          return;
        }

        if (stream) {
          setState({
            loading: false,
            error: null,
            blob: null,
            url,
          });
          return;
        }

        const fetchResponse = await fetch(url);
        if (!fetchResponse.ok) {
          throw new Error(
            `Failed to fetch file (status ${fetchResponse.status})`,
          );
        }
        const blob = await fetchResponse.blob();
        if (cancelledRef.current) return;

        const entry = store(key, blob);
        acquiredKeyRef.current = key;
        setState({
          loading: false,
          error: null,
          blob: entry.blob,
          url: entry.url,
        });
      } catch (error) {
        if (cancelledRef.current) return;
        const message =
          error instanceof Error ? error.message : "Failed to load file";
        setState({ loading: false, error: message, blob: null, url: null });
      }
    };

    void run();

    return () => {
      cancelledRef.current = true;
      if (acquiredKeyRef.current) {
        release(acquiredKeyRef.current);
        acquiredKeyRef.current = null;
      }
    };
  }, [source, attempt, triggerDownloadFile, stream]);

  return {
    ...state,
    retry: () => setAttempt((n) => n + 1),
  };
}
